import React, { useState, useEffect, useMemo } from "react";
import Chart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import {
  StatCard,
  DateRangePicker,
  ReportHeader,
  ReportPanel,
} from "../../components";
import { DateRange } from "../../types";
import { ReportService } from "../../services/report-service";

// ==================== HELPERS ====================

const today = new Date();
const startOfYear = new Date(today.getFullYear(), 0, 1);
const formatDate = (d: Date) => d.toISOString().split("T")[0];

// ==================== Icons ====================

const UsersIcon = () => (
  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
  </svg>
);

const UserAddIcon = () => (
  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
  </svg>
);

const MONTH_LABELS = ["T1", "T2", "T3", "T4", "T5", "T6", "T7", "T8", "T9", "T10", "T11", "T12"];

// ==================== COMPONENT ====================

export function MarketingDashboard() {
  const [dateRange, setDateRange] = useState<DateRange>({
    from: startOfYear,
    to: today,
  });

  // ==================== API hooks ====================
  const {
    fetch: fetchCustomers,
    response: customersResponse,
    loading: customersLoading,
  } = ReportService.customersList();

  // ==================== Fetch data ====================
  const fetchAllData = () => {
    fetchCustomers({
      limit: 10000,
      page: 1,
      sort_by: "created_at",
      sort_order: 1,
      from_date: formatDate(dateRange.from),
      to_date: formatDate(dateRange.to),
    });
  };

  useEffect(() => {
    fetchAllData();
  }, [dateRange.from?.toISOString(), dateRange.to?.toISOString()]);

  // ==================== Derived data from API ====================
  const customersList = customersResponse?.data?.list ?? [];
  const totalCustomers = customersResponse?.data?.total_record ?? 0;

  // Group customers by created_at month for growth chart
  const customerGrowthData = useMemo(() => {
    const newByMonth = new Array(12).fill(0);
    const cumulativeByMonth = new Array(12).fill(0);

    customersList.forEach((customer: any) => {
      if (customer.created_at) {
        const createdDate = new Date(customer.created_at);
        if (createdDate.getFullYear() === today.getFullYear()) {
          const month = createdDate.getMonth();
          newByMonth[month] += 1;
        }
      }
    });

    // Calculate cumulative (running total)
    let runningTotal = 0;
    for (let i = 0; i < 12; i++) {
      runningTotal += newByMonth[i];
      cumulativeByMonth[i] = runningTotal;
    }

    return {
      categories: MONTH_LABELS,
      newCustomers: newByMonth,
      returningCustomers: cumulativeByMonth,
    };
  }, [customersList]);

  // Count new customers this month
  const newCustomersThisMonth = useMemo(() => {
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();
    return customersList.filter((customer: any) => {
      if (!customer.created_at) return false;
      const d = new Date(customer.created_at);
      return d.getMonth() === currentMonth && d.getFullYear() === currentYear;
    }).length;
  }, [customersList]);

  const isLoading = customersLoading;

  // ==================== Chart configs ====================

  // Customer Growth Chart
  const customerGrowthChartOptions: ApexOptions = {
    chart: {
      type: "area",
      toolbar: { show: false },
      fontFamily: "Inter, sans-serif",
      stacked: true,
    },
    stroke: { curve: "smooth", width: 2 },
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.5,
        opacityTo: 0.1,
        stops: [0, 100],
      },
    },
    colors: ["#22c55e", "#3b82f6"],
    dataLabels: { enabled: false },
    xaxis: {
      categories: customerGrowthData.categories,
      labels: { style: { fontSize: "11px" } },
    },
    yaxis: {
      labels: { style: { fontSize: "11px" } },
    },
    legend: {
      position: "top",
      horizontalAlign: "right",
    },
    tooltip: {
      y: { formatter: (val: number) => `${val} khách` },
    },
  };

  const customerGrowthChartSeries = [
    { name: "Tổng lũy kế", data: customerGrowthData.returningCustomers },
    { name: "Khách mới", data: customerGrowthData.newCustomers },
  ];

  return (
    <div className="p-6 space-y-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <ReportHeader
        title="Báo Cáo Marketing"
        description="Phân tích khách hàng"
      >
        <DateRangePicker
          value={dateRange}
          onChange={setDateRange}
        />
      </ReportHeader>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <StatCard
          title="Tổng khách hàng"
          value={customersLoading ? "..." : totalCustomers.toLocaleString()}
          icon={<UsersIcon />}
          variant="primary"
          subtitle="Tổng số khách hàng trong hệ thống"
        />
        <StatCard
          title="Khách mới tháng này"
          value={customersLoading ? "..." : newCustomersThisMonth}
          icon={<UserAddIcon />}
          variant="success"
          subtitle="Khách hàng đăng ký mới trong tháng"
        />
      </div>

      {/* Customer Growth */}
      <ReportPanel title="Tăng Trưởng Khách Hàng">
        {customersLoading ? (
          <div className="flex items-center justify-center h-[300px]">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500" />
          </div>
        ) : (
          <Chart
            options={customerGrowthChartOptions}
            series={customerGrowthChartSeries}
            type="area"
            height={300}
          />
        )}
      </ReportPanel>
    </div>
  );
}

export default MarketingDashboard;
